import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Cog6ToothIcon, 
  UserIcon, 
  BellIcon, 
  ShieldCheckIcon,
  PaintBrushIcon,
  KeyIcon,
  ServerStackIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { api, useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import PageHeader from '../ui/PageHeader';
import AdminSystemConfig from './AdminSystemConfig';
import UserAvatar from '../ui/UserAvatar';
import Select from '../ui/Select';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Switch from '../ui/Switch';
import { ModalActions } from '../ui/ModalFooter';

function SettingsPanel() {
  const { user, setUser, refreshUser } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const failedUpdateMessage = t.settings.failedUpdate;
  const avatarInputRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('system');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [privacyLoading, setPrivacyLoading] = useState(true);
  const [privacySaving, setPrivacySaving] = useState(false);
  const [settings, setSettings] = useState({
    profile: {
      username: user?.username || '',
      email: user?.email || '',
      firstName: '',
      lastName: '',
      bio: '',
      avatar: ''
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      weeklyReports: true,
      newReferrals: true,
      conversions: true
    },
    privacy: {
      publicProfile: false,
      showStats: true,
      allowMessages: true,
      dataSharing: false,
      optOutRight: false,
    },
    appearance: {
      language,
      timezone: 'America/New_York',
      dateFormat: 'MM/DD/YYYY'
    },
    system: {
      name: user?.username || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });

  const tabs = [
    { id: 'system', name: t.settings.systemSettings, icon: KeyIcon },
    ...(user?.is_admin
      ? [{ id: 'platform', name: t.settings.platformConfig || 'System Configuration', icon: ServerStackIcon }]
      : []),
    { id: 'profile', name: t.settings.profile, icon: UserIcon },
    { id: 'notifications', name: t.settings.notifications, icon: BellIcon },
    { id: 'privacy', name: t.settings.privacy, icon: ShieldCheckIcon },
    { id: 'appearance', name: t.settings.appearance, icon: PaintBrushIcon },
  ];

  const handleSave = (section) => {
    toast.success(`${section} ${t.settings.savedSuccessfully}`);
  };

  useEffect(() => {
    let cancelled = false;

    const loadPrivacySettings = async () => {
      try {
        const response = await api.get('/user/privacy-settings');
        if (cancelled) return;
        if (response.data?.privacy) {
          setSettings((prev) => ({
            ...prev,
            privacy: {
              ...prev.privacy,
              ...response.data.privacy,
            },
          }));
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.error || failedUpdateMessage);
        }
      } finally {
        if (!cancelled) {
          setPrivacyLoading(false);
        }
      }
    };

    void loadPrivacySettings();
    return () => {
      cancelled = true;
    };
  }, [failedUpdateMessage]);

  const handleSavePrivacy = async () => {
    setPrivacySaving(true);
    try {
      const response = await api.put('/user/privacy-settings', settings.privacy);
      if (response.data?.privacy) {
        setSettings((prev) => ({
          ...prev,
          privacy: {
            ...prev.privacy,
            ...response.data.privacy,
          },
        }));
      }
      toast.success(`${t.settings.privacy} ${t.settings.savedSuccessfully}`);
    } catch (error) {
      toast.error(error.response?.data?.error || t.settings.failedUpdate);
    } finally {
      setPrivacySaving(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t.settings.avatarInvalidType || 'Please choose a valid image file.');
      event.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t.settings.avatarTooLarge || 'Image is too large. Maximum size is 5 MB.');
      event.target.value = '';
      return;
    }

    setAvatarUploading(true);
    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const response = await api.post('/user/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data?.user) {
        setUser(response.data.user);
      } else {
        await refreshUser();
      }
      toast.success(t.settings.avatarUploaded || 'Profile photo updated.');
    } catch (error) {
      toast.error(error.response?.data?.error || t.settings.avatarUploadFailed || 'Could not upload photo.');
    } finally {
      setAvatarUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    setAvatarUploading(true);
    try {
      const response = await api.delete('/user/avatar');
      if (response.data?.user) {
        setUser(response.data.user);
      } else {
        await refreshUser();
      }
      toast.success(t.settings.avatarRemoved || 'Profile photo removed.');
    } catch (error) {
      toast.error(error.response?.data?.error || t.settings.avatarUploadFailed || 'Could not remove photo.');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSystemSettingsSubmit = () => {
    const { name, currentPassword, newPassword, confirmPassword } = settings.system;
    
    // Validations
    if (!name.trim()) {
      toast.error(t.settings.nameRequired);
      return;
    }

    if (newPassword) {
      if (!currentPassword) {
        toast.error(t.settings.currentPasswordRequired);
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error(t.settings.passwordsNoMatch);
        return;
      }
      if (newPassword.length < 6) {
        toast.error(t.settings.passwordTooShort);
        return;
      }
    }

    // Prepare changes object
    const changes = {
      name: name.trim(),
    };

    if (newPassword) {
      changes.currentPassword = currentPassword;
      changes.newPassword = newPassword;
    }

    setPendingChanges(changes);
    setShowConfirmModal(true);
  };

  const handleConfirmChanges = async () => {
    if (!pendingChanges) return;

    setIsLoading(true);
    try {
      const response = await fetch('/api/user/update-profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(pendingChanges)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(t.settings.settingsUpdated);
        
        // Clear password fields
        setSettings(prev => ({
          ...prev,
          system: {
            ...prev.system,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }
        }));
        
        setShowConfirmModal(false);
        setPendingChanges(null);
      } else {
        toast.error(data.error || t.settings.failedUpdate);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t.settings.errorUpdating);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelChanges = () => {
    setShowConfirmModal(false);
    setPendingChanges(null);
  };

  return (
    <div className="content-flow">
      <PageHeader
        eyebrow="Settings"
        icon={Cog6ToothIcon}
        title={t.settings.title}
        subtitle={t.settings.subtitle}
      />

      {/* Settings Content */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-4 xl:gap-6">
        {/* Tabs Sidebar */}
        <div className="lg:col-span-1">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="card-premium p-4"
          >
      <nav className={`max-lg:mobile-scroll-x lg:mx-0 lg:flex lg:flex-col lg:gap-0 lg:space-y-1 lg:overflow-visible lg:px-0 lg:pb-0`}>
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                <motion.button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className={`ios-tab ${isActive ? 'ios-tab-active' : ''}`}
                >
                  {isActive && (
                    <motion.div
                      layoutId="settingsActiveTab"
                      className="ios-tab-indicator"
                      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    />
                  )}
                  <tab.icon className="ios-tab-icon h-5 w-5 flex-shrink-0" />
                  <span className="max-lg:whitespace-nowrap lg:truncate">{tab.name}</span>
                </motion.button>
              );})}
            </nav>
          </motion.div>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="card-premium p-6"
          >
            <AnimatePresence mode="wait">
              {activeTab === 'system' && (
                    <motion.div
                      key="system"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <div>
                        <h3 className="text-lg font-semibold text-secondary-900  mb-4">{t.settings.systemSettings}</h3>
                        <p className="text-sm text-secondary-600  mb-6">{t.settings.updateAccount}</p>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="form-label">{t.settings.name}</label>
                            <Input
                              type="text"
                              value={settings.system.name}
                              onChange={(e) => handleChange('system', 'name', e.target.value)}
                              placeholder={t.settings.yourName}
                            />
                          </div>

                          <div className="border-t border-secondary-200  pt-4 mt-6">
                            <h4 className="text-md font-semibold text-secondary-900  mb-4">{t.settings.changePassword}</h4>
                            <p className="text-sm text-secondary-500  mb-4">{t.settings.leaveEmpty}</p>
                            
                            <div className="space-y-4">
                              <div>
                                <label className="form-label">{t.settings.currentPassword}</label>
                                <Input
                                  type="password"
                                  value={settings.system.currentPassword}
                                  onChange={(e) => handleChange('system', 'currentPassword', e.target.value)}
                                  placeholder={t.settings.enterCurrentPassword}
                                  autoComplete="current-password"
                                />
                              </div>

                              <div>
                                <label className="form-label">{t.settings.newPassword}</label>
                                <Input
                                  type="password"
                                  value={settings.system.newPassword}
                                  onChange={(e) => handleChange('system', 'newPassword', e.target.value)}
                                  placeholder={t.settings.enterNewPassword}
                                  autoComplete="new-password"
                                />
                              </div>

                              <div>
                                <label className="form-label">{t.settings.confirmNewPassword}</label>
                                <Input
                                  type="password"
                                  value={settings.system.confirmPassword}
                                  onChange={(e) => handleChange('system', 'confirmPassword', e.target.value)}
                                  placeholder={t.settings.confirmNewPasswordPlaceholder}
                                  autoComplete="new-password"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <Button onClick={handleSystemSettingsSubmit}>
                        {t.settings.saveChanges}
                      </Button>
                    </motion.div>
                  )}

                  {activeTab === 'platform' && user?.is_admin && (
                    <motion.div
                      key="platform"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <div>
                        <h3 className="text-lg font-semibold text-secondary-900  mb-4">
                          {t.settings.platformConfig || 'System Configuration'}
                        </h3>
                        <p className="text-sm text-secondary-600  mb-6">
                          {t.settings.platformConfigDesc || 'Manage preconfigured pipeline statuses and other system defaults.'}
                        </p>
                        <AdminSystemConfig />
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'profile' && (
                    <motion.div
                      key="profile"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <div>
                        <h3 className="text-lg font-semibold text-secondary-900  mb-4">
                          {t.settings.profilePhoto || 'Profile photo'}
                        </h3>
                        <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
                          <div className="relative flex-shrink-0 overflow-hidden rounded-3xl border-4 border-white shadow-glow ">
                            <UserAvatar
                              src={user?.avatar_url}
                              alt={user?.username || t.common.user}
                              size="xl"
                            />
                          </div>
                          <div className="flex-1 space-y-3 text-center sm:text-left">
                            <input
                              ref={avatarInputRef}
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                              className="hidden"
                              onChange={handleAvatarUpload}
                            />
                            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
                              <Button
                                type="button"
                                onClick={() => avatarInputRef.current?.click()}
                                disabled={avatarUploading}
                                icon={PhotoIcon}
                              >
                                {avatarUploading
                                  ? (t.settings.uploadingPhoto || 'Uploading...')
                                  : (t.settings.uploadPhoto || 'Upload photo')}
                              </Button>
                              {user?.avatar_url ? (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={() => void handleRemoveAvatar()}
                                  disabled={avatarUploading}
                                >
                                  {t.settings.removePhoto || 'Remove photo'}
                                </Button>
                              ) : null}
                            </div>
                            <p className="text-xs text-secondary-500 ">
                              {t.settings.avatarHint || 'Square images work best. Your photo appears in the sidebar menu.'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-secondary-900  mb-4">{t.settings.personalInfo}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="form-label">{t.settings.firstName}</label>
                            <Input
                              type="text"
                              value={settings.profile.firstName}
                              onChange={(e) => handleChange('profile', 'firstName', e.target.value)}
                              placeholder={t.settings.firstName}
                            />
                          </div>
                          <div>
                            <label className="form-label">{t.settings.lastName}</label>
                            <Input
                              type="text"
                              value={settings.profile.lastName}
                              onChange={(e) => handleChange('profile', 'lastName', e.target.value)}
                              placeholder={t.settings.lastName}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="form-label">{t.settings.bio}</label>
                            <Input
                              as="textarea"
                              variant="textarea"
                              value={settings.profile.bio}
                              onChange={(e) => handleChange('profile', 'bio', e.target.value)}
                              rows={3}
                              placeholder={t.settings.tellUs}
                            />
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => handleSave(t.settings.profile)}>
                        {t.settings.saveProfile}
                      </Button>
                    </motion.div>
                  )}

                  {activeTab === 'notifications' && (
                    <motion.div
                      key="notifications"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <div>
                        <h3 className="text-lg font-semibold text-secondary-900  mb-4">{t.settings.notificationPreferences}</h3>
                        <div className="space-y-4">
                          {Object.entries(settings.notifications).map(([key, value]) => (
                            <div key={key} className="toggle-row">
                              <div>
                                <p className="font-medium text-secondary-900 ">
                                  {key === 'emailNotifications' && t.settings.emailNotifications}
                                  {key === 'pushNotifications' && t.settings.pushNotifications}
                                  {key === 'weeklyReports' && t.settings.weeklyReports}
                                  {key === 'newReferrals' && t.settings.newReferrals}
                                  {key === 'conversions' && t.settings.conversions}
                                </p>
                                <p className="text-sm text-secondary-500 ">
                                  {key === 'emailNotifications' && t.settings.emailNotificationsDesc}
                                  {key === 'pushNotifications' && t.settings.pushNotificationsDesc}
                                  {key === 'weeklyReports' && t.settings.weeklyReportsDesc}
                                  {key === 'newReferrals' && t.settings.newReferralsDesc}
                                  {key === 'conversions' && t.settings.conversionsDesc}
                                </p>
                              </div>
                              <Switch
                                checked={value}
                                onChange={(next) => handleChange('notifications', key, next)}
                                aria-label={
                                  key === 'emailNotifications' ? t.settings.emailNotifications
                                  : key === 'pushNotifications' ? t.settings.pushNotifications
                                  : key === 'weeklyReports' ? t.settings.weeklyReports
                                  : key === 'newReferrals' ? t.settings.newReferrals
                                  : t.settings.conversions
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button onClick={() => handleSave(t.settings.notifications)}>
                        {t.settings.savePreferences}
                      </Button>
                    </motion.div>
                  )}

                  {activeTab === 'privacy' && (
                    <motion.div
                      key="privacy"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <div>
                        <h3 className="text-lg font-semibold text-secondary-900  mb-4">{t.settings.privacySettings}</h3>
                        {privacyLoading ? (
                          <p className="text-sm text-secondary-500 ">{t.common.loading || 'Loading...'}</p>
                        ) : (
                        <div className="space-y-4">
                          {Object.entries(settings.privacy).map(([key, value]) => (
                            <div key={key} className="toggle-row">
                              <div>
                                <p className="font-medium text-secondary-900 ">
                                  {key === 'publicProfile' && t.settings.publicProfile}
                                  {key === 'showStats' && t.settings.showStats}
                                  {key === 'allowMessages' && t.settings.allowMessages}
                                  {key === 'dataSharing' && t.settings.dataSharing}
                                  {key === 'optOutRight' && t.settings.optOutRight}
                                </p>
                                <p className="text-sm text-secondary-500 ">
                                  {key === 'publicProfile' && t.settings.publicProfileDesc}
                                  {key === 'showStats' && t.settings.showStatsDesc}
                                  {key === 'allowMessages' && t.settings.allowMessagesDesc}
                                  {key === 'dataSharing' && t.settings.dataSharingDesc}
                                  {key === 'optOutRight' && t.settings.optOutRightDesc}
                                </p>
                              </div>
                              <Switch
                                checked={value}
                                onChange={(next) => handleChange('privacy', key, next)}
                                aria-label={
                                  key === 'publicProfile' ? t.settings.publicProfile
                                  : key === 'showStats' ? t.settings.showStats
                                  : key === 'allowMessages' ? t.settings.allowMessages
                                  : key === 'optOutRight' ? t.settings.optOutRight
                                  : t.settings.dataSharing
                                }
                              />
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                      <Button onClick={() => void handleSavePrivacy()} disabled={privacyLoading || privacySaving}>
                        {privacySaving ? (t.settings.updating || 'Updating...') : t.settings.savePrivacy}
                      </Button>
                    </motion.div>
                  )}

                  {activeTab === 'appearance' && (
                    <motion.div
                      key="appearance"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <div>
                        <h3 className="text-lg font-semibold text-secondary-900  mb-4">{t.settings.appearanceLocalization}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="form-label">{t.settings.language}</label>
                            <Select
                              value={language}
                              onChange={(e) => {
                                const next = e.target.value;
                                changeLanguage(next);
                                handleChange('appearance', 'language', next);
                                toast.success(t.settings.languageChanged);
                              }}
                              className="input-field"
                            >
                              <option value="en">{t.settings.languageEn}</option>
                              <option value="es">{t.settings.languageEs}</option>
                            </Select>
                          </div>
                          <div>
                            <label className="form-label">{t.settings.timezone}</label>
                            <Select
                              value={settings.appearance.timezone}
                              onChange={(e) => handleChange('appearance', 'timezone', e.target.value)}
                              className="input-field"
                            >
                              <option value="America/New_York">New York (Eastern)</option>
                              <option value="America/Chicago">Chicago (Central)</option>
                              <option value="America/Denver">Denver (Mountain)</option>
                              <option value="America/Los_Angeles">Los Angeles (Pacific)</option>
                              <option value="Europe/London">London</option>
                              <option value="UTC">UTC</option>
                            </Select>
                          </div>
                          <div>
                            <label className="form-label">{t.settings.dateFormat}</label>
                            <Select
                              value={settings.appearance.dateFormat}
                              onChange={(e) => handleChange('appearance', 'dateFormat', e.target.value)}
                              className="input-field"
                            >
                              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                            </Select>
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => handleSave(t.settings.appearance)}>
                        {t.settings.saveAppearance}
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={handleCancelChanges}
        title={t.settings.confirmChanges}
        size="default"
        footer={
          <ModalActions
            onCancel={handleCancelChanges}
            onConfirm={handleConfirmChanges}
            cancelLabel={t.common.cancel}
            confirmLabel={isLoading ? t.settings.updating : t.common.confirm}
            loading={isLoading}
          />
        }
      >
        <div className="space-y-4">
          <p className="text-secondary-700 ">
            {t.settings.confirmChangesDesc}
          </p>
          
          <div className="surface-elevated p-4 space-y-2">
            {pendingChanges?.name && (
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-secondary-600 ">{t.settings.name}:</span>
                <span className="text-sm text-secondary-900 ">{pendingChanges.name}</span>
              </div>
            )}
            {pendingChanges?.newPassword && (
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-secondary-600 ">{t.settings.password}:</span>
                <span className="text-sm text-secondary-900 ">{t.settings.password} {t.common.willBeChanged || 'Will be changed'}</span>
              </div>
            )}
          </div>
        </div>
      </Modal>
      </div>
    );
  }

export default SettingsPanel;



